import { useState, useEffect, useCallback } from 'react'

import { CloseButton, Divider, Modal, Textarea, TextInput } from '@mantine/core'
import { useOs } from '@mantine/hooks'

import { useMutation, useQuery } from '@redwoodjs/web'

import { useAuth } from 'src/auth'
import DNButton from 'src/components/Buttons/Button/DNButton'
import { toast } from 'src/components/Overlays/Toast/Toast'
import {
  AssigneeMenu,
  DueDaysMenu,
  IncludeAssignedEmployeeMenu,
  PriorityMenu,
  ReminderDaysMenu,
  TypeMenu,
} from 'src/components/Shared/TaskMenus'
import {
  CREATE_TASK_TEMPLATE,
  GET_TASK_TEMPLATE,
  UPDATE_TASK_TEMPLATE,
} from 'src/graphql/taskTemplates.graphql'
import { TaskPriority, TaskType } from 'src/graphql/types/tasks'
import IconCornerDownRight from 'src/icons/IconCornerDownRight'
import { cn } from 'src/utils'

interface TaskTemplateModalProps {
  taskTemplate?: any
  edit: boolean
  isOpen: boolean
  onClose: () => void
  refetchQueries: any[]
}

const TaskTemplateModal = ({
  taskTemplate,
  edit,
  isOpen,
  onClose,
  refetchQueries,
}: TaskTemplateModalProps) => {
  const os = useOs()
  const { currentUser } = useAuth()
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [priority, setPriority] = useState(TaskPriority.MEDIUM)
  const [type, setType] = useState(TaskType.OTHER)
  const [defaultDueInHours, setDefaultDueInHours] = useState<
    number | undefined
  >(24)
  const [defaultReminderInHours, setDefaultReminderInHours] = useState<
    number | undefined
  >(24)
  const [includeAssignedEmployee, setIncludeAssignedEmployee] = useState(false)
  const [assignees, setAssignees] = useState<string[]>([])
  const isValid = title.trim() !== ''

  const { data } = useQuery(GET_TASK_TEMPLATE, {
    variables: {
      taskTemplateId: taskTemplate?.taskTemplateId,
    },
    skip: !edit || !taskTemplate?.taskTemplateId,
  })

  // Reset form when modal opens in create mode
  useEffect(() => {
    if (isOpen && !edit) {
      setTitle('')
      setDescription('')
      setPriority(TaskPriority.MEDIUM)
      setType(TaskType.OTHER)
      setDefaultDueInHours(24)
      setDefaultReminderInHours(24)
      setIncludeAssignedEmployee(false)
      setAssignees([])
    }
  }, [isOpen, edit, currentUser?.employeeId])

  // Set form values when editing
  useEffect(() => {
    if (edit && (data?.taskTemplate || taskTemplate)) {
      const template = data?.taskTemplate || taskTemplate
      setTitle(template.title || '')
      setDescription(template.description || '')
      setPriority(template.priority || TaskPriority.MEDIUM)
      setType(template.type || TaskType.OTHER)
      setDefaultDueInHours(template.defaultDueInHours ?? undefined)
      setDefaultReminderInHours(template.defaultReminderInHours ?? undefined)
      setIncludeAssignedEmployee(template.includeAssignedEmployee || false)
      setAssignees(template.assignees?.map((a) => a.employeeId) || [])
    }
  }, [edit, data, taskTemplate])

  const [createTaskTemplate, { loading }] = useMutation(CREATE_TASK_TEMPLATE, {
    refetchQueries,
    onCompleted: () => {
      onClose()
      toast('Task template created successfully', 'success')
    },
    onError: () => {
      toast('Error creating task template', 'error')
    },
  })

  const [updateTaskTemplate, { loading: updateLoading }] = useMutation(
    UPDATE_TASK_TEMPLATE,
    {
      refetchQueries,
      onCompleted: () => {
        onClose()
        toast('Task template updated successfully', 'success')
      },
      onError: () => {
        toast('Error updating task template', 'error')
      },
    }
  )

  // A template's due and reminder are both offsets from task creation, so a
  // reminder offset larger than the due offset would alert after the task is
  // already due. Keep them coupled: lowering the due pulls an over-long
  // reminder in to match (remind right when it's due), and the reminder can
  // never be set beyond the due.
  const handleDueChange = (hours: number | undefined) => {
    setDefaultDueInHours(hours)
    if (
      hours != null &&
      defaultReminderInHours != null &&
      defaultReminderInHours > hours
    ) {
      setDefaultReminderInHours(hours)
    }
  }

  const handleReminderChange = (hours: number | undefined) => {
    if (
      hours != null &&
      defaultDueInHours != null &&
      hours > defaultDueInHours
    ) {
      setDefaultReminderInHours(defaultDueInHours)
    } else {
      setDefaultReminderInHours(hours)
    }
  }

  const handleSubmit = useCallback(() => {
    if (isValid) {
      const input = {
        title,
        ...(description && { description }),
        type,
        priority,
        defaultDueInHours: defaultDueInHours ?? null,
        defaultReminderInHours: defaultReminderInHours ?? null,
        includeAssignedEmployee,
        ...(assignees.length > 0 && { assigneeIds: assignees }),
      }

      if (edit && taskTemplate?.taskTemplateId) {
        updateTaskTemplate({
          variables: {
            taskTemplateId: taskTemplate.taskTemplateId,
            input,
          },
        })
      } else {
        createTaskTemplate({
          variables: {
            input,
          },
        })
      }
    }
  }, [
    isValid,
    title,
    description,
    type,
    priority,
    defaultDueInHours,
    defaultReminderInHours,
    includeAssignedEmployee,
    assignees,
    edit,
    taskTemplate,
    createTaskTemplate,
    updateTaskTemplate,
  ])

  // Add global event listener for keyboard shortcut
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (!isOpen) return

      const isMac = os === 'macos'
      const modKey = isMac ? event.metaKey : event.ctrlKey

      if (modKey && event.key === 'Enter') {
        event.preventDefault()
        handleSubmit()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [handleSubmit, os, isOpen])

  return (
    <Modal
      opened={isOpen}
      onClose={onClose}
      yOffset={100}
      overlayProps={{
        backgroundOpacity: 0.2,
        blur: 5,
      }}
      stackId="task-template-modal"
      size="50%"
      withCloseButton={false}
      classNames={{
        body: 'flex flex-col gap-2',
      }}
    >
      <div className="flex flex-row items-start justify-between">
        <TextInput
          variant="unstyled"
          placeholder="Task template title"
          className="w-full font-semibold"
          value={title}
          onChange={(event) => setTitle(event.currentTarget.value)}
          size="lg"
          data-testid="task-template-title-input"
        />
        <CloseButton onClick={onClose} />
      </div>

      <Textarea
        variant="unstyled"
        placeholder="Description"
        autosize
        minRows={4}
        maxRows={10}
        value={description}
        onChange={(event) => setDescription(event.currentTarget.value)}
        size="sm"
      />

      <div className="flex w-full flex-row items-center gap-1 overflow-auto">
        <PriorityMenu priority={priority} setPriority={setPriority} />
        <TypeMenu type={type} setType={setType} />
        <AssigneeMenu assignees={assignees} setAssignees={setAssignees} />
        <DueDaysMenu
          defaultDueInHours={defaultDueInHours}
          setDefaultDueInHours={handleDueChange}
        />
        <ReminderDaysMenu
          defaultReminderInHours={defaultReminderInHours}
          setDefaultReminderInHours={handleReminderChange}
        />
        <IncludeAssignedEmployeeMenu
          includeAssignedEmployee={includeAssignedEmployee}
          setIncludeAssignedEmployee={setIncludeAssignedEmployee}
        />
      </div>

      <Divider />

      <div
        className={cn(
          'flex flex-row justify-end gap-2',
          !isValid && 'opacity-70'
        )}
      >
        <DNButton
          variant="primary"
          onClick={handleSubmit}
          loading={edit ? updateLoading : loading}
        >
          {edit ? 'Update template' : 'Create template'}
          <div className="flex flex-row items-center text-doubleNickel-gray-200">
            {os === 'macos' ? '⌘' : 'Ctrl'}
            <IconCornerDownRight className="h-3 w-3 scale-x-[-1] fill-none stroke-doubleNickel-gray-200" />
          </div>
        </DNButton>
      </div>
    </Modal>
  )
}

export default TaskTemplateModal
